import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  Checkbox,
  Link,
  SkeletonPlaceholder
} from 'carbon-components-react';
import { useSelector } from 'react-redux';
import TreeView, {
  TreeNode
} from 'carbon-components-react/lib/components/TreeView';
import {
  DocumentPdf16,
  WatsonHealthDicomOverlay16,
  Document16,
  Doc16,
  DocumentPdf24,
  Ppt16,
  Xls16,
  Txt16,
  Csv16,
  Svg16,
  Gif16,
  Jpg16,
  Png16,
  Zip16
} from '@carbon/icons-react';
import PDFPreview from './PDFPreview';
import { getAttachmentPreviewUrl } from 'services/enquiry.service';
import {
  LocalDownloadAdvancedOptionsData,
  LocalDownloadAdvancedOptionsDataFile,
  LocalDownloadAdvancedOptions,
  SelectedPage,
  RenderNodeProps
} from 'types';
import { selectIsLoadingDownloadAdvancedOptions } from 'redux/enquiries/selectors';
import { isMobileOnly } from 'react-device-detect';

const EXTENSION_ICON: Record<string, React.ReactElement> = {
  pdf: <DocumentPdf16 />,
  doc: <Doc16 />,
  ppt: <Ppt16 />,
  xls: <Xls16 />,
  csv: <Csv16 />,
  txt: <Txt16 />,
  svg: <Svg16 />,
  png: <Png16 />,
  jpg: <Jpg16 />,
  gif: <Gif16 />,
  zip: <Zip16 />
};

type Props = {
  shareId: string | undefined;
  enquiryId?: number | string;
  setIsAccordionOpen: (isOpen: boolean) => void;
  type: 'zip' | 'pdf';
  isAccordionOpen: boolean;
  localDownloadAdvancedOptions: LocalDownloadAdvancedOptions;
  setLocalDownloadAdvancedOptions: React.Dispatch<
    React.SetStateAction<LocalDownloadAdvancedOptions>
  >;
};

const recalculateCheckboxValue = (item: LocalDownloadAdvancedOptionsData) => {
  let checkCount = 0;

  for (const attachment of item.attachments) {
    let shouldCount = false,
      isIndeterminate = false;

    if (attachment.isSupported) {
      if (attachment.extension === 'pdf') {
        if ('pageNumbers' in attachment) {
          if (attachment.pageNumbers?.length) {
            isIndeterminate = true;
            shouldCount = true;
          } else {
            attachment.isSelected = false;
          }
        } else {
          attachment.isSelected = true;
          shouldCount = true;
        }
      } else {
        shouldCount = attachment.isSelected;
      }
    }

    attachment.indeterminate = isIndeterminate;
    shouldCount && checkCount++;
  }

  if (checkCount === item.supportAttachmentCount) {
    item.isSelected = true;
    item.indeterminate = item.attachments.some(
      (attachment) => attachment.indeterminate
    );
  } else {
    item.indeterminate = Boolean(checkCount);
    item.isSelected = Boolean(checkCount);
  }
};

const calculateSelectedCount = (data: LocalDownloadAdvancedOptionsData[]) => {
  let count = 0;
  for (const item of data) {
    if (item.isCompleted) {
      for (const attachment of item.attachments) {
        if (attachment.isSupported) {
          count += attachment.isSelected ? 1 : 0;
        }
      }
    }
  }
  return count;
};

const setPageNumbers = (
  attachment: LocalDownloadAdvancedOptionsDataFile,
  checked: boolean,
  extension: string
) => {
  if (extension === 'pdf') {
    if (checked) {
      delete attachment.pageNumbers;
    } else {
      attachment.pageNumbers = [];
    }
  }
};

const calculateHeight = (compenentRef: any): { height: number } => {
  if (!compenentRef.current) return { height: 0 };
  return {
    height:
      compenentRef.current.advancedOptionsAccordion?.offsetHeight -
      32 -
      compenentRef.current.accordion?.offsetHeight -
      compenentRef.current.startContainer?.offsetHeight -
      compenentRef.current.endContainer?.offsetHeight
  };
};

export const AdvancedOptions: React.FC<Props> = (props: Props) => {
  const {
    setIsAccordionOpen,
    type,
    isAccordionOpen,
    setLocalDownloadAdvancedOptions,
    localDownloadAdvancedOptions,
    enquiryId,
    shareId
  } = props;
  const isLoadingDownloadAdvancedOptions = useSelector(
    selectIsLoadingDownloadAdvancedOptions
  );
  const [selectedCount, setSelectedCount] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedPage, setSelectedPage] = useState<SelectedPage | null>(null);
  const [size, setSize] = useState<{ height: number }>({
    height: 0
  });
  const filesRef = useRef<{ [key: number]: string }>({});
  const compenentRef = useRef<any>({});
  const selectionStyle = isMobileOnly ? {} : { height: size?.height || 0 };

  useEffect(() => {
    if (isAccordionOpen) {
      setSelectedCount(
        calculateSelectedCount(localDownloadAdvancedOptions.data) || 0
      );
      setSize(calculateHeight(compenentRef));
    }
  }, [isAccordionOpen, localDownloadAdvancedOptions.data]);

  useEffect(() => {
    const update = () => {
      setSize(calculateHeight(compenentRef));
    };
    update();

    if ('visualViewport' in window) {
      window.visualViewport?.addEventListener('resize', update);
      return () => window.visualViewport?.removeEventListener('resize', update);
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const handleParentCheckboxChange = useCallback(
    (index: number) => (checked: boolean) => {
      setLocalDownloadAdvancedOptions((prev) => {
        const newData = [...prev.data];
        const item = newData[index];

        if (item.indeterminate) checked = false;

        item.isSelected = checked;
        item.indeterminate = false;

        for (const attachment of item.attachments) {
          if (attachment.isSupported) {
            attachment.isSelected = checked;
            attachment.indeterminate = false;

            setPageNumbers(attachment, checked, attachment.extension || '');
          }
        }

        return { ...prev, data: newData };
      });
    },
    [setLocalDownloadAdvancedOptions]
  );

  const handleChildCheckboxChange = useCallback(
    (parentIndex: number, attachmentIndex: number, extension: string) => (
      checked: boolean
    ) => {
      setLocalDownloadAdvancedOptions((prev) => {
        const newData = [...prev.data];
        const item = newData[parentIndex];
        const attachment = item.attachments[attachmentIndex];
        // if indeterminate then set to false
        if (attachment.indeterminate) checked = false;

        attachment.isSelected = checked;
        attachment.indeterminate = false;

        setPageNumbers(attachment, checked, extension);

        recalculateCheckboxValue(item);

        return { ...prev, data: newData };
      });
    },
    [setLocalDownloadAdvancedOptions]
  );

  const handleMailCheckboxChange = useCallback(
    (parentIndex: number, attachmentIndex: number) => (checked: boolean) => {
      setLocalDownloadAdvancedOptions((prev) => {
        const newData = [...prev.data];
        const item = newData[parentIndex];

        item.attachments[attachmentIndex].isSelected = checked;
        recalculateCheckboxValue(item);
        return { ...prev, data: newData };
      });
    },
    [setLocalDownloadAdvancedOptions]
  );

  const handlePageNumberChange = useCallback(
    (parentIndex: number, attachmentIndex: number) => (
      pageNumbers: number[],
      isAllChecked: boolean
    ) => {
      setLocalDownloadAdvancedOptions((prev) => {
        const newData = [...prev.data];
        const item = newData[parentIndex];
        const attachment = item.attachments[attachmentIndex];

        if (pageNumbers.length === 0 && isAllChecked) {
          delete attachment.pageNumbers;
          attachment.isSelected = true;
        } else {
          attachment.pageNumbers = pageNumbers;
          attachment.isSelected = Boolean(pageNumbers.length);
        }

        recalculateCheckboxValue(item);

        return { ...prev, data: newData };
      });
    },
    [setLocalDownloadAdvancedOptions]
  );

  const handleOnToggle = useCallback(
    (parentIndex: number) => (event: any, node: any) => {
      setLocalDownloadAdvancedOptions((prev) => {
        const newData = [...prev.data];
        newData[parentIndex].isExpanded = node.isExpanded;
        return { ...prev, data: newData };
      });
    },
    [setLocalDownloadAdvancedOptions]
  );

  const handlePreviewUrl = useCallback(
    (fileId: number, parentIndex: number, attachmentIndex: number) => async (
      event: any,
      node: any
    ) => {
      let isOpen = node.isExpanded;

      if (!filesRef.current[fileId]) {
        const res = await getAttachmentPreviewUrl(
          fileId,
          shareId,
          String(enquiryId)
        );
        filesRef.current[fileId] = res.url;
        isOpen = true;
      }

      setSelectedPage(null);

      setLocalDownloadAdvancedOptions((prev) => {
        const newData = [...prev.data];

        for (const parent of newData) {
          for (const attachment of parent.attachments) {
            if ('isExpanded' in attachment) {
              attachment.isExpanded = false;
            }
          }
        }

        newData[parentIndex].attachments[attachmentIndex].isExpanded = isOpen;
        return { ...prev, data: newData };
      });
    },
    [setLocalDownloadAdvancedOptions, shareId, enquiryId]
  );

  const handleClickSelectAll = useCallback(
    (isAllChecked: boolean) => () => {
      setLocalDownloadAdvancedOptions((prev) => ({
        ...prev,
        data: prev.data.map((item: LocalDownloadAdvancedOptionsData) => {
          item.isSelected = isAllChecked;
          item.indeterminate = false;
          item.attachments.map(
            (attachment: LocalDownloadAdvancedOptionsDataFile) => {
              if (attachment.isSupported) {
                attachment.isSelected = isAllChecked;
                attachment.indeterminate = false;

                setPageNumbers(
                  attachment,
                  isAllChecked,
                  attachment.extension || ''
                );
              }

              return attachment;
            }
          );
          return item;
        })
      }));
    },
    [setLocalDownloadAdvancedOptions]
  );

  const handleCollapse = useCallback(() => {
    setIsCollapsed((prevCollapsed) => {
      const newState = !prevCollapsed;

      setLocalDownloadAdvancedOptions((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          data: prev.data.map((item: LocalDownloadAdvancedOptionsData) => ({
            ...item,
            isExpanded: prevCollapsed,
            attachments: item.attachments.map(
              (attachment: LocalDownloadAdvancedOptionsDataFile) => {
                // if collapse all then close all expanded attachment, if expand all just first layer open attachment dont open
                if ('isExpanded' in attachment && newState) {
                  return {
                    ...attachment,
                    isExpanded: prevCollapsed
                  };
                }
                return attachment;
              }
            )
          }))
        };
      });

      return newState;
    });
  }, [setLocalDownloadAdvancedOptions]);

  const renderNode = useCallback(
    ({
      id,
      icon,
      label,
      isSupported,
      extension,
      isSelected,
      indeterminate = false,
      onChange,
      isExpanded = false,
      attachmentIndex,
      pageNumbers,
      parentIndex
    }: RenderNodeProps) => {
      const isPDF = extension === 'pdf';
      const commonProps = {
        key: id,
        isExpanded,
        label: (
          <Checkbox
            {...(onChange ? { onChange: onChange } : {})}
            {...(isSupported ? {} : { disabled: true })}
            id={`${id}`}
            checked={isSelected}
            indeterminate={indeterminate}
            title={label}
            labelText={
              <div className="flex-center">
                {icon}
                <span className="file-name">{label}</span>
              </div>
            }
          />
        )
      };

      if (isPDF && type === 'pdf') {
        const fileId = Number(id);
        return (
          <TreeNode
            {...commonProps}
            onToggle={handlePreviewUrl(fileId, parentIndex, attachmentIndex)}
          >
            <TreeNode
              className="pdf-container"
              label={
                filesRef.current[fileId] ? (
                  <PDFPreview
                    onPageSelection={handlePageNumberChange(
                      parentIndex,
                      attachmentIndex
                    )}
                    fileId={fileId}
                    selectedFileId={
                      selectedPage?.fileId === id
                        ? selectedPage?.selectedFileId
                        : ''
                    }
                    pdfUrl={filesRef.current[fileId]}
                    setSelectedPage={setSelectedPage}
                    pageNumbers={pageNumbers}
                  />
                ) : (
                  <SkeletonPlaceholder className="skeleton" />
                )
              }
            />
          </TreeNode>
        );
      }

      return <TreeNode {...commonProps} />;
    },
    [type, handlePreviewUrl, selectedPage, handlePageNumberChange]
  );

  const appendRef = (key: string) => (el: any) => {
    if (el) compenentRef.current = { ...compenentRef.current, [key]: el };
  };

  return (
    <div
      ref={appendRef('advancedOptionsAccordion')}
      style={{ flex: 1, minHeight: 0 }}
    >
      <Accordion align="start" className="advanced-options-accordion">
        <AccordionItem
          title={
            <div ref={appendRef('accordion')}>
              <Link className="link-style underline">
                {isAccordionOpen
                  ? 'Hide advanced options'
                  : 'Show advanced options'}
              </Link>
              <br />
              <span>
                {type === 'zip'
                  ? 'Customise your response pack by specifying the files to exclude from the ZIP file.'
                  : 'Customise your response pack by selecting the files to include in the PDF file.'}
              </span>
            </div>
          }
          onHeadingClick={({ isOpen }) => setIsAccordionOpen(isOpen)}
          open={isAccordionOpen}
        >
          {isLoadingDownloadAdvancedOptions ? (
            <SkeletonPlaceholder
              className="skeleton"
              style={{ width: '100%' }}
            />
          ) : localDownloadAdvancedOptions.data.length > 0 ? (
            <>
              <div
                className="start-end-container"
                ref={appendRef('startContainer')}
              >
                <div>
                  <strong>
                    {type === 'zip'
                      ? 'Select the files you want to include in the ZIP'
                      : 'Select the files and pages you want to include in the PDF'}
                  </strong>
                </div>

                <div className="collapse">
                  <Link className="link-style" onClick={handleCollapse}>
                    {isCollapsed ? 'Expand all' : 'Collapse all'}
                  </Link>
                </div>
              </div>
              <div className={`adv-container ${type}`}>
                <div className="left" style={selectionStyle}>
                  <TreeView
                    label=""
                    selected={[]}
                    className="non-interactive-treeview"
                  >
                    {localDownloadAdvancedOptions?.data?.map(
                      (item, parentIndex) => {
                        const {
                          isExpanded,
                          organisationName,
                          isSelected,
                          isCompleted,
                          indeterminate,
                          attachments
                        } = item;

                        return isCompleted ? (
                          <TreeNode
                            isExpanded={isExpanded}
                            key={organisationName}
                            onToggle={handleOnToggle(parentIndex)}
                            label={
                              <Checkbox
                                onChange={handleParentCheckboxChange(
                                  parentIndex
                                )}
                                id={organisationName}
                                checked={isSelected}
                                indeterminate={indeterminate}
                                title={organisationName}
                                labelText={organisationName}
                              />
                            }
                          >
                            {attachments?.map((attachment, aIndex) => {
                              const {
                                type,
                                responseId,
                                name,
                                id,
                                isSelected,
                                isSupported,
                                extension,
                                indeterminate,
                                pageNumbers,
                                isExpanded
                              } = attachment;

                              if (type === 'divider') {
                                return (
                                  <TreeNode
                                    key={id}
                                    label={<strong>{name}</strong>}
                                  />
                                );
                              }

                              if (type === 'response' || type === 'mail') {
                                return renderNode({
                                  id,
                                  isSupported,
                                  icon: <WatsonHealthDicomOverlay16 />,
                                  label: name,
                                  isSelected,
                                  onChange: handleMailCheckboxChange(
                                    parentIndex,
                                    aIndex
                                  ),
                                  parentIndex: parentIndex,
                                  attachmentIndex: aIndex
                                });
                              }

                              if (type === 'attachment') {
                                return renderNode({
                                  responseId,
                                  parentIndex: parentIndex,
                                  attachmentIndex: aIndex,
                                  id: id,
                                  extension: extension,
                                  icon: EXTENSION_ICON[extension || ''] || (
                                    <Document16 />
                                  ),
                                  label: name,
                                  isSelected,
                                  isExpanded,
                                  indeterminate,
                                  pageNumbers,
                                  isSupported,
                                  ...(isSupported
                                    ? {
                                        onChange: handleChildCheckboxChange(
                                          parentIndex,
                                          aIndex,
                                          extension || ''
                                        )
                                      }
                                    : {})
                                });
                              }
                            })}
                          </TreeNode>
                        ) : (
                          <TreeNode
                            className="pending-response"
                            key={organisationName}
                            label={
                              <div className="start-end-container full-width disabled">
                                <div className="min-width-0">
                                  <Checkbox
                                    className="company-name"
                                    disabled
                                    id={organisationName}
                                    labelText={organisationName}
                                  />
                                </div>
                                <div>
                                  <span className="awaiting-text">
                                    Awaiting responses
                                  </span>
                                </div>
                              </div>
                            }
                          />
                        );
                      }
                    )}
                  </TreeView>
                </div>
                {type === 'pdf' && !isMobileOnly && (
                  <div className="right" style={selectionStyle}>
                    <div className="title-container">
                      <strong>PDF page preview</strong>
                    </div>
                    <div className="preview-container">
                      {selectedPage ? (
                        <div
                          className={`image-wrapper ${
                            selectedPage.isPortrait
                              ? 'portrait'
                              : selectedPage.isLandscape
                              ? 'horizontal'
                              : 'square'
                          }`}
                        >
                          <div className="aspect-ratio-container">
                            <img
                              src={URL.createObjectURL(selectedPage.pageBlob)}
                              alt={`Page ${selectedPage.pageNumber}`}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="placeholder-container">
                          <DocumentPdf24 />
                          <p>Click on the PDF thumbnail to preview the page</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div
                className="start-end-container"
                ref={appendRef('endContainer')}
              >
                <div>
                  <Link
                    className="link-style underline mr-10"
                    onClick={handleClickSelectAll(true)}
                  >
                    Select all
                  </Link>
                  <Link
                    className="link-style underline"
                    onClick={handleClickSelectAll(false)}
                  >
                    Deselect all
                  </Link>
                </div>

                <div>
                  {`${selectedCount} out of ${
                    localDownloadAdvancedOptions?.totalAttachmentCount
                  } file${
                    localDownloadAdvancedOptions?.totalAttachmentCount
                      ? 's'
                      : ''
                  } selected`}
                </div>
              </div>
            </>
          ) : null}
        </AccordionItem>
      </Accordion>
    </div>
  );
};

AdvancedOptions.displayName = 'AdvancedOptions';

export default React.memo(AdvancedOptions);
